import { useEffect } from 'react';
import type { AmountType, CostType, Visibility } from 'src/apis/monolith/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { handleErrorWithNotification } from 'src/utils/handle-error';
import { labelsToOptions } from 'src/utils/labels-to-options';

export function assertAmountType(value: string): asserts value is AmountType {
  if (value === 'KILO' || value === 'MEGA') {
    // oxlint-disable-next-line no-unused-vars
    type TypeCheck<T extends AmountType = typeof value> = T;
  } else {
    throw new Error(`${value} is not of type "AmountType".`);
  }
}

export const amountTypeLabels: Record<AmountType, string> = {
  KILO: 'kWh',
  MEGA: 'MWh',
};

export const amountTypeOptions = labelsToOptions(amountTypeLabels);

export function assertCostType(value: string): asserts value is CostType {
  if (value === 'UNIT' || value === 'SUBUNIT') {
    // oxlint-disable-next-line no-unused-vars
    type TypeCheck<T extends CostType = typeof value> = T;
  } else {
    throw new Error(`${value} is not of type "CostType".`);
  }
}

export const costTypeLabels: Record<CostType, string> = {
  SUBUNIT: 'Subunit',
  UNIT: 'Unit',
};

export const costTypeOptions = labelsToOptions(costTypeLabels);

export type PlatformSettings = {
  favoriteNetworkPoints: Visibility;
  recentActivities: Visibility;
  cost: CostType;
  amount: AmountType;
};

export function useOptionalPlatformSettings() {
  const [_, setReauthenticate] = useReauthenticate();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const platformSettings = useAxios(
    (axios, baseConfig) =>
      axios.request<PlatformSettings>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.monolithApiUrl}/shipperUsers/settings/platform`,
        method: 'GET',
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // when we get a 401, we don't want to show the full 401 page like we usually do,
        // because it could be a bit too disruptive. instead we show our regular notification.
        if (isServerError(error, 401)) {
          handleErrorWithNotification({ error, setReauthenticate });
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!monolithUser?.isShipper) return;
    platformSettings.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  if (!monolithUser?.isShipper) return;

  return platformSettings;
}

export type OptionalPlatformSettingsRequest = ReturnType<
  typeof useOptionalPlatformSettings
>;
